// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import { AccessDeniedPage } from '../pages/admin/AccessDeniedPage.js';
import { SkeletonLoader } from './SkeletonLoader.js';
import { apiClient } from '../services/api-client.js';
import { MFA_STEP_UP_ROUTE, mfaStepUpPath } from '../utils/mfa-step-up.js';
import { PASSWORD_CHANGE_REQUIRED_ROUTE } from '../utils/password-change-required.js';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: string;
  anyPermission?: string[];
}

/**
 * Route wrapper that redirects unauthenticated users.
 * Also redirects to /setup if the setup wizard has not been completed or skipped.
 */
export function ProtectedRoute({ children, permission, anyPermission }: ProtectedRouteProps) {
  const { isAuthenticated, loading, user } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissionContext();
  const location = useLocation();
  const [wizardChecked, setWizardChecked] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || loading) return;

    // Skip check when already on the setup page to avoid infinite redirect
    if (location.pathname === '/setup') {
      setWizardChecked(true);
      return;
    }

    apiClient
      .getWizard()
      .then((wizard: any) => {
        const done = wizard?.status === 'completed' || wizard?.status === 'skipped';
        setNeedsSetup(!done);
      })
      .catch(() => {
        // If wizard fetch fails, don't block the user
        setNeedsSetup(false);
      })
      .finally(() => setWizardChecked(true));
  }, [isAuthenticated, loading, location.pathname]);

  if (loading || (isAuthenticated && !wizardChecked)) {
    return <SkeletonLoader variant="card" count={1} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (needsSetup) {
    return <Navigate to="/setup" replace />;
  }

  /*
   * Sprint 63.7.0 — a session held at the enrolment interstitial goes there and
   * nowhere else.
   *
   * ABOVE the permission checks, and above nothing else: the server is the
   * authority here (`authenticate` answers 403 `MFA_ENROLMENT_REQUIRED` on
   * every path but six), and this redirect exists so the user sees the screen
   * that fixes it rather than a permission-denied page for a route they are in
   * fact allowed to reach.
   *
   * It is deliberately BELOW the setup-wizard redirect. A tenant mid-setup has
   * no MFA policy yet, and sending a first administrator to enrol before the
   * organisation exists would be a loop.
   */
  if (user?.mfa_enrolment_required && location.pathname !== '/mfa-enrolment-required') {
    return <Navigate to="/mfa-enrolment-required" replace />;
  }

  /*
   * Sprint 63.6.3, gate E12.5 pass 2 — the other hold. A session that has not
   * presented its second factor (`mfa_required`, set by a sign-in that came back
   * held, e.g. a federated one the organisation does not take the provider's
   * factor for) goes to the code prompt, carrying where it was going. The
   * server is still the authority: a held session that reached here without the
   * flag (a reload) is sent by the api-client on its first 403 `MFA_REQUIRED`.
   * The enrolment hold above wins if both are somehow set, as in `LoginPage`.
   */
  if (user?.mfa_required && location.pathname !== MFA_STEP_UP_ROUTE) {
    return <Navigate to={mfaStepUpPath(`${location.pathname}${location.search}`)} replace />;
  }

  /*
   * Sprint 63.29.0 (#541) — an administrator requires a new password. BELOW the
   * MFA holds, matching the server's order (`authenticate` answers the MFA holds
   * first), and above the permission checks for the same reason they are: the
   * server admits this session to three paths, so the person belongs on the
   * step that releases it, not on a permission-denied page.
   */
  if (user?.password_change_required && location.pathname !== PASSWORD_CHANGE_REQUIRED_ROUTE) {
    return <Navigate to={PASSWORD_CHANGE_REQUIRED_ROUTE} replace />;
  }

  // Permission check
  if (permission && !hasPermission(permission)) {
    return <AccessDeniedPage />;
  }

  if (anyPermission && anyPermission.length > 0 && !hasAnyPermission(anyPermission)) {
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
}
